import { Box, Stack, Typography, SvgIcon as MUISvgIcon } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { type FC, type ResponsiveImageType, ResponsiveImage, Link } from ".."
import { LinkType } from "../../graphql/types"

export type Author = {
  title: string
  photo?: ResponsiveImageType
  position: string
  description?: string
  link?: LinkType
}

const AuthorPreview: FC<Author> = ({ title, photo, position, description }) => {
  return (
    <Box
      width="100%"
      display="flex"
      position="relative"
      flexDirection={{ xs: "column", md: "row" }}
      minHeight={120}
      sx={(theme) => ({
        background: theme.palette["tertiary.200"].main,
        padding: { xs: 0, lg: theme.spacing(7.5, 10) },
      })}
    >
      <AuthorPreviewHeading />
      <AuthorInfo title={title} photo={photo} position={position} description={description} />
    </Box>
  )
}

export default AuthorPreview

/** A sub-component for the heading of the author preview */
const AuthorPreviewHeading: FC = () => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={5}
      width="100%"
      padding={(theme) => ({ xs: theme.spacing(4.5, 5), lg: 0 })}
      borderBottom={(theme) => ({ xs: `1px solid ${theme.palette["neutral.200"].main}`, md: 0 })}
    >
      <Box
        display="flex"
        position="relative"
        pr={5}
        sx={{
          borderRight: (theme) => `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        }}
      >
        <Link type="overlay" href="/inspiration" aria-label="Globetrotting" />
        <MUISvgIcon viewBox="0 0 26 36" sx={{ width: 26, height: 36 }}>
          <path
            d="M11.6195 20.324C6.01358 20.0998 4.077 19.2436 4.34201 16.7566C6.01358 17.4905 8.21516 17.9186 11.0079 17.9186C18.4484 17.9186 22.0362 14.6978 22.0362 10.2131C22.0362 7.80763 20.9966 5.62643 18.7746 4.15871V4.13832C18.6319 4.0364 18.4688 3.95486 18.3057 3.85293C18.265 3.83255 18.2242 3.81216 18.1834 3.79178C18.1223 3.75101 18.0611 3.73062 18 3.68985C16.8992 3.11907 15.5538 2.69099 13.9841 2.44637H13.9637C13.8007 2.42598 13.6376 2.4056 13.4745 2.38521C13.4337 2.38521 13.393 2.38521 13.3522 2.36483C13.2095 2.34444 13.0668 2.34444 12.9241 2.32406C12.8426 2.32406 12.7814 2.30367 12.6999 2.30367C12.5776 2.30367 12.4552 2.28329 12.3329 2.28329C12.2106 2.28329 12.1087 2.2629 11.9864 2.2629C11.8845 2.2629 11.8029 2.2629 11.701 2.24252C11.4768 2.24252 11.2729 2.24252 11.0487 2.24252C3.4043 2.22213 0 5.85067 0 10.1927C0 12.7 1.03964 14.7997 3.28199 16.1859C1.50849 17.0013 0 18.6321 0 20.8744C0 24.6253 3.22083 25.9911 9.09172 26.4191C13.3522 26.7249 17.9184 27.153 17.9184 30.2923C17.9184 33.2277 14.5549 34.512 11.3341 34.5935C7.11437 34.7158 3.62853 31.6173 6.17666 27.3976C2.40543 27.6015 0 29.1507 0 31.1281C0 34.0228 4.13816 36.0001 11.1098 36.0001C19.9365 36.0001 24.8086 32.6366 24.8086 27.2957C24.8086 21.9548 19.5085 20.6502 11.6195 20.324ZM11.0283 3.60831C14.0453 3.60831 15.1665 6.58453 15.1665 9.98882C15.1665 13.5562 14.0453 16.5324 11.0283 16.5324C7.92977 16.5324 6.89014 13.5562 6.89014 9.98882C6.89014 6.58453 7.92977 3.60831 11.0283 3.60831Z"
            fill="#F05B2A"
          />
          <path
            d="M23.7282 6.64552L25.624 0L18.9173 1.65119L22.2605 3.32276L23.7282 6.64552Z"
            fill="#727272"
          />
        </MUISvgIcon>
      </Box>
      <Box>
        <Typography variant="body.lg">Globetrotting</Typography>
      </Box>
    </Stack>
  )
}

/** A sub-component for the author info */
const AuthorInfo: FC<Author> = ({ title, photo, position }) => {
  const photoWithAlt: ResponsiveImageType = {
    ...photo,
    variants:
      photo?.variants.map((img) => ({
        ...img,
        alt: title,
      })) ?? [],
  }

  return (
    <Box
      display="flex"
      gap={5}
      minWidth={{ xs: "100%", md: 355 }}
      padding={(theme) => ({ xs: theme.spacing(5), md: 0 })}
    >
      <Box display="flex" alignItems="center">
        {photoWithAlt && (
          <ResponsiveImage image={photoWithAlt} borderRadius={1.5} sx={{ width: 60, height: 60 }} />
        )}
        {!photo && (
          <Stack
            alignItems="center"
            justifyContent="center"
            bgcolor={(theme) => theme.palette["secondary.300"].main}
            borderRadius={1.5}
            sx={{ width: 60, height: 60 }}
          >
            <DefaultPhoto />
          </Stack>
        )}
      </Box>
      <Stack direction="column" justifyContent="center">
        <Typography variant="body.sm" component="h2" sx={{ fontWeight: 500 }}>
          {`By ${title}`}
        </Typography>
        <Typography variant="body.sm">{position}</Typography>
      </Stack>
    </Box>
  )
}

const DefaultPhoto: FC = () => (
  <MUISvgIcon viewBox="0 0 38 38" sx={{ width: 38, height: 38 }}>
    <path
      d="M34.0415 7.91667C34.9155 7.91667 35.6248 7.20733 35.6248 6.33333C35.6248 5.45933 34.9155 4.75 34.0415 4.75C33.1675 4.75 32.4582 5.45933 32.4582 6.33333C32.4582 7.20733 33.1675 7.91667 34.0415 7.91667ZM30.0832 4.75C31.3926 4.75 32.4582 3.68442 32.4582 2.375C32.4582 1.06558 31.3926 0 30.0832 0C28.7738 0 27.7082 1.06558 27.7082 2.375C27.7082 3.68442 28.7738 4.75 30.0832 4.75ZM30.0832 1.58333C30.5202 1.58333 30.8748 1.938 30.8748 2.375C30.8748 2.812 30.5202 3.16667 30.0832 3.16667C29.6462 3.16667 29.2915 2.812 29.2915 2.375C29.2915 1.938 29.6462 1.58333 30.0832 1.58333ZM13.3616 26.9563C13.9474 26.9167 14.5475 26.923 15.146 26.9847C15.5403 28.7676 17.1204 30.0833 18.9998 30.0833H23.7498C25.9317 30.0833 27.7082 28.3068 27.7082 26.125V21.375C27.7082 19.1932 25.9317 17.4167 23.7498 17.4167H4.74984C2.568 17.4167 0.791504 19.1932 0.791504 21.375V26.125C0.791504 28.3068 2.568 30.0833 4.74984 30.0833H9.49984C11.3998 30.0833 12.9816 28.7581 13.3616 26.9563ZM11.8653 26.2073C11.8194 27.493 10.7808 28.5 9.49984 28.5H4.74984C3.44042 28.5 2.37484 27.4344 2.37484 26.125V21.375C2.37484 20.0656 3.44042 19 4.74984 19H23.7498C25.0593 19 26.1248 20.0656 26.1248 21.375V26.125C26.1248 27.4344 25.0593 28.5 23.7498 28.5H18.9998C17.7363 28.5 16.7008 27.5073 16.6391 26.239C16.6201 25.8638 16.3414 25.5534 15.9709 25.4948C15.3423 25.3967 14.7138 25.3476 14.0915 25.3476C13.5738 25.3476 13.0576 25.3824 12.5509 25.4505C12.1678 25.5027 11.878 25.821 11.8653 26.2073ZM7.9165 20.5833H6.33317C5.02375 20.5833 3.95817 21.6489 3.95817 22.9583V24.5417C3.95817 24.9787 4.31284 25.3333 4.74984 25.3333C5.18684 25.3333 5.5415 24.9787 5.5415 24.5417V22.9583C5.5415 22.5213 5.89617 22.1667 6.33317 22.1667H7.9165C8.3535 22.1667 8.70817 21.812 8.70817 21.375C8.70817 20.938 8.3535 20.5833 7.9165 20.5833ZM37.0894 9.87525C36.9453 9.6425 36.6904 9.5 36.4165 9.5H33.2498C32.9759 9.5 32.721 9.64408 32.5769 9.87683C32.4328 10.1096 32.4186 10.4009 32.5421 10.6463L34.0415 13.6452V19.7917C34.0415 28.0852 27.2933 34.8333 18.9998 34.8333H18.0625C17.7348 33.9134 16.8639 33.25 15.8332 33.25H14.2498C12.9404 33.25 11.8748 34.3156 11.8748 35.625C11.8748 36.9344 12.9404 38 14.2498 38H15.8332C16.8639 38 17.7348 37.3366 18.0625 36.4167H18.9998C28.1673 36.4167 35.6248 28.9592 35.6248 19.7917V13.6436L37.1243 10.6448C37.2478 10.4009 37.2335 10.108 37.0894 9.87525ZM15.8332 36.4167H14.2498C13.8128 36.4167 13.4582 36.062 13.4582 35.625C13.4582 35.188 13.8128 34.8333 14.2498 34.8333H15.8332C16.2702 34.8333 16.6248 35.188 16.6248 35.625C16.6248 36.062 16.2702 36.4167 15.8332 36.4167Z"
      fill="#31456B"
    />
  </MUISvgIcon>
)
